// packages/contracts/src/refusal.ts

/**
 * The reasons the pro-ration domain can refuse to compute a bill.
 *
 * ⚠ I3: the gateway used to triage these by REGEX over `error.message` —
 * `/no entitlement covers/` became a 422 and *everything else* became a bare
 * 500 `{"message":"internal error"}`. Three consequences, all proven live
 * against the running gateway before this type existed:
 *
 *  - A service date earlier than every rule version (`no rule in force for
 *    scheme MSO on service date 2024-06-01`) answered **500**. So did an
 *    officer on scheme `CWM`, and so did one on `NSF`. `003_domain.sql` permits
 *    six schemes and only MSO and CCS have rule versions seeded, so **four of
 *    six schemes render as an internal error** the moment a real entitlement
 *    feed lands — a refusal the caller could act on, reported as a server
 *    fault it cannot.
 *  - Matching on message TEXT means an edit to the wording in
 *    `process-bill.ts` silently turns the working 422 back into a 500. The
 *    coupling is invisible: nothing fails, nothing warns, and the endpoint
 *    quietly starts lying about whose fault it is.
 *  - A 500 is the status that says "the server broke, retry later". A bill that
 *    can never be pro-rated until PSD authors a rule version is not that.
 *
 * So a refusal carries a `kind` the route switches on, and the message is for
 * humans only — never for control flow. Adding a member here forces every
 * exhaustive mapping over it to be updated, which is the whole point.
 *
 * ⚠ What is deliberately NOT in this list: the officer-mismatch throw in
 * `prorate()` (a bill pro-rated against another officer's entitlement). That is
 * an internal WIRING defect, not a refusal a caller can act on — it means this
 * process crossed two officers — and it must stay a plain `Error` so it can
 * never be mapped to a 4xx and never have its two officer ids rendered to a
 * caller. It reaches the gateway's global handler and becomes a sanitised 500,
 * which is the honest answer for it.
 */
export const REFUSAL_KINDS = [
  /** No entitlement period covers this officer on this service date. */
  'NO_ENTITLEMENT',
  /** An entitlement was supplied, but its window does not contain the service date. */
  'ENTITLEMENT_NOT_COVERING',
  /** Two or more entitlement periods cover the same date — a source-data defect. */
  'AMBIGUOUS_ENTITLEMENT',
  /** No rule version is in force for this scheme on this service date. */
  'NO_RULE_IN_FORCE',
  /** The scheme is not confirmed for use (NSF, pending clarification Q-04). */
  'SCHEME_NOT_ENABLED',
  /**
   * No entry of a pro-ration configuration parameter is in force for this key
   * on this service date (Annex A 40–47).
   *
   * The counterpart of `NO_RULE_IN_FORCE`, one layer down: the rule set exists
   * and the rule fired, but a value the rule needs — a pegged ward rate for
   * that ward class, a grace period for that bill type — has no entry covering
   * the date of medical service. PSD authors it; nothing here can invent it,
   * and a silent default in pro-ration is an unfunded payment
   * (`docs/design/rule-governance.md` §2, "Exhaustive").
   */
  'NO_REFERENCE_VALUE_IN_FORCE',
  /**
   * The bill reached PRS later than the maximum grace period in force for its
   * DATE OF MEDICAL SERVICE (Annex A 45).
   *
   * ⚠ Its own kind rather than a flavour of the one above, because the two need
   * different remedies and the message has to name them: an absent parameter is
   * fixed by PSD authoring one, whereas an exceeded grace period is a decision
   * about this bill — reject it with a reason, or supersede the grace period
   * with a window that covers this service date. A refusal that ships without
   * its way out just moves the trap one door along.
   */
  'BILL_OUTSIDE_GRACE_PERIOD',
  /**
   * Task 7 (Annex A 44) — a service-injury bill was billed under a ward class
   * below the minimum `MIN_WARD_SERVICE_INJURY` requires on this service date.
   *
   * ⚠ Its own kind rather than `NO_REFERENCE_VALUE_IN_FORCE`: a value IS in
   * force here (the floor was found and compared against), so the earlier kind
   * would be a factually wrong reason — that kind means no entry covers the
   * date at all. This one means an entry was found and the bill's own ward
   * fails it, and the message names the ward it needed so the remedy is
   * concrete (I3: never let a reader guess a threshold from a bare refusal).
   */
  'WARD_BELOW_SERVICE_INJURY_MINIMUM',
  /**
   * Task 8 (Annex A 41) — the pegged ward rate cap fired and this pro-ration
   * split has no way to honour the excess-bearer policy in force.
   *
   * ⚠ **ONE kind, two distinct causes, deliberately not split further** (unlike
   * `ADJUSTMENT_TARGET_UNAVAILABLE`'s reason for folding four causes together,
   * this is the OPPOSITE choice made for the opposite reason: nothing here is a
   * disclosure risk, so the two causes stay separable in the message text even
   * though they share a kind):
   *
   *  - no `PEGGED_RATE_EXCESS_BEARER` value is in force at all — the deliberate
   *    gap Q-31 asks the Authority to close; or
   *  - a value IS in force and names `PROVIDER`, which this split cannot
   *    execute: `subsidy + coPayment` must equal the bill's full amount
   *    (enforced by `003_domain.sql`'s own CHECK), so making a Healthcare
   *    Service Provider bear the excess would need a SETTLEMENT-level recovery
   *    instruction against the provider, which does not exist in PRS2 today.
   *
   * Reusing `NO_REFERENCE_VALUE_IN_FORCE` for the second cause would be a
   * refusal that lies about its own reason — a value is not absent, it is
   * present and unactionable by this engine — which is exactly the kind of
   * refusal I3 exists to prevent.
   */
  'PEGGED_RATE_EXCESS_BEARER_UNSUPPORTED',
  /**
   * Task 9 (Annex A 43) — the officer's MBIM record has expired as at the
   * bill's date of medical service: `record.issuedOn + MBIM_MAX_VALIDITY`
   * (resolved on the SERVICE date) falls before it.
   *
   * ⚠ Its own kind rather than `NO_REFERENCE_VALUE_IN_FORCE`: a value IS in
   * force here (the maximum validity was found and applied), and the record
   * itself exists — this means the computed expiry has passed, which is a
   * different fact from either "no record" (not this kind at all; see
   * `assessMbimValidity`'s own "absence is not a refusal" argument) or "no
   * parameter covers this date" (that kind, unchanged).
   */
  'MBIM_RECORD_EXPIRED',

  // ══════════════════════════════════════════════════════════════════════════
  // THE FILE-EXCHANGE PATH — Annex A 2–8.
  //
  // ⚠ These extend the SAME mechanism rather than adding a second one. The
  // obvious alternative is a parallel `FileRejectionReason` enum "because a
  // file has no HTTP status", and it is wrong for the reason this type exists:
  // a refusal is a thing the caller can act on, and whether it arrived over
  // HTTP or in a batch changes the transport, not the refusal. Two schemes
  // means two sets of consumers, two exhaustiveness guarantees, and a remedy
  // table that covers one of them.
  //
  // Some members below are unreachable over HTTP today. That is fine and the
  // `Record<RefusalKind, …>` tables stay TOTAL — an entry that is never read is
  // cheap; a kind with no entry is a runtime `undefined` on a path that renders
  // to a counterparty.
  //
  // ⚠ A `pg` error is still never a `DomainRefusal` (see the class's own
  // contract). On this path that rule stops being merely good hygiene: the
  // remedy keyed off these kinds is rendered into a file a counterparty reads,
  // so a driver string reaching one is a disclosure, not just an ugly message.
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * The record does not conform to the published layout — a field is missing,
   * mistyped, out of bounds, or carries a character the delimited format cannot
   * represent (P2C11 §14.6(a)–(d)).
   *
   * Also carried by a whole FILE that is structurally unreadable: invalid
   * UTF-8, an absent or malformed header or trailer, or a data record whose
   * field count means the amount column cannot be located. In that case no
   * record in the file is processed.
   */
  'RECORD_MALFORMED',
  /** No officer in PRS matches the identifier on this record. */
  'UNKNOWN_OFFICER',
  /**
   * A bill with this submitter reference has already been received from this
   * provider.
   *
   * ⚠ A REPORTED outcome, never a silent skip. It is recognised because an
   * `INSERT` conflicted with `UNIQUE (hsp_agency_code, source_reference)` — not
   * because code looked first — so it holds across a re-send with different
   * line endings, a re-ordered file, or one bill split across two days'
   * deliveries, none of which a digest over the bytes can see.
   */
  'DUPLICATE_SOURCE_REFERENCE',
  /**
   * ★ The original bill this adjustment names cannot be adjusted.
   *
   * ⚠ **ONE kind covering FIVE internal causes, deliberately, and it must never
   * be split.** The original does not exist; it exists but is outside this
   * counterparty's scope; it is `REJECTED`; it is itself an adjustment; or it
   * was COMPENSATED (C4, final review — `REVERSED` in
   * `packages/contracts/src/cross-context.ts`'s `TargetUnavailability`: a saga
   * step downstream of its creation failed and the effect was reversed).
   * Distinguishing them outwardly would rebuild the enumeration oracle that
   * `get()`'s 404 discipline exists to close — a counterparty could walk
   * references and learn which ones are real. `reason_detail` and the audit trail
   * recover the distinction internally, where it belongs.
   *
   * The rule this is an instance of: **any two internal causes whose difference
   * would disclose the existence of something the counterparty may not see are
   * ONE kind.**
   *
   * ⚠ **"Never computed" is its OWN cause, deliberately NOT here** — it is
   * {@link ORIGINAL_RESOLUTION_UNREPRODUCIBLE}, its own kind. This docstring
   * said five until the adjustment path was built (Annex A 3, 4); the plan's D2
   * gives that case its own kind and its own remedy, and the rule above permits
   * it: it is reachable ONLY for a bill the caller can already read in full,
   * having passed the scope-filtered lookup, so it discloses nothing the
   * caller does not already have. The other five (now including `REVERSED`)
   * are reachable for bills the caller may not see, which is exactly the
   * difference.
   */
  'ADJUSTMENT_TARGET_UNAVAILABLE',
  /**
   * The adjustment carries a date of medical service that disagrees with the
   * original bill's.
   *
   * ⚠ Refused rather than ignored, and refused rather than trusted. An
   * adjustment inherits the original's service date
   * (`docs/design/domain-language.md` §1); a correction for services delivered
   * on a different date is a NEW bill. Silently ignoring the field would accept
   * a record whose sender believes it means something else, and trusting it
   * would re-resolve entitlement on a date the original was never judged
   * against. The disagreement is itself the signal.
   */
  'ADJUSTMENT_SERVICE_DATE_MISMATCH',
  /**
   * Applying this adjustment would take the bill's running total below zero.
   *
   * A negative bill is a provider owing the government money — a refund flow,
   * which nothing in Annex A describes.
   */
  'ADJUSTMENT_BELOW_ZERO',
  /**
   * ★ C2 (final review, PROVEN BY EXECUTION) — the original bill's stored split
   * is not the flat rate's split. A subsidy cap already fired on it
   * (community-hospital ineligibility, the child length-of-stay cap, the
   * pegged ward rate excess) and rewrote `subsidy`/`coPayment` away from
   * `splitByBasisPoints(amount, appliedRule.rate)`, with nothing on the row
   * distinguishing that from an ordinary flat split.
   *
   * ⚠ **Refused rather than silently recomputed**, because recomputing from
   * the flat rate is precisely the defect: proven live, a $1,000.00 bill at
   * 80% ruled CH-ineligible (stored `{ subsidy: 0, coPayment: 100000 }`) took
   * an ordinary +$1.00 adjustment and answered `{ subsidy: 80080, coPayment:
   * 20020 }` — a one-dollar correction moving $800.80 from the officer to the
   * public purse, with every existing guard silent because none of them was
   * built to catch a REWRITTEN split. See `applyDeltaToSplit`
   * (`packages/contracts/src/money.ts`) for the guard and the full
   * reproduction.
   *
   * ⚠ Unlike `ADJUSTMENT_BELOW_ZERO`, this is not a property of the DELTA —
   * it is a property of the ORIGINAL, discovered before the delta is even
   * looked at, and it fires identically for an upward or a downward
   * adjustment.
   */
  'ADJUSTMENT_ORIGINAL_CAPPED',
  /**
   * The original bill has no stored pro-ration provenance, so there is nothing
   * for the adjustment to reproduce.
   *
   * An adjustment applies the rule id, version and rate the ORIGINAL was
   * computed under; a bill with none has never been computed. Its remedy names
   * the compute route, in the same style `uncomputedRefusal` already does.
   */
  'ORIGINAL_RESOLUTION_UNREPRODUCIBLE',
  /**
   * ★★★ WAVE 12 (P5) — the adjustment-indicator REGISTER and the grammar's own
   * mapping disagree about what this indicator means on this date of medical
   * service.
   *
   * `app.reference_value` for `ADJUSTMENT_INDICATOR` (Annex A story 40) holds
   * `NB`/`UA`/`DA` against the `app.bill.bill_type` each identifies, and
   * `INDICATOR_BILL_TYPE` in `services/transfer/src/grammars/hsp-layout.ts`
   * holds the same three pairs in code. **Two copies of one rule** — the S-011
   * defect class this repository has already paid for twice — and until this
   * wave only the code copy could stop money, so superseding the register
   * changed nothing at all and story 40’s whole purpose was defeated.
   *
   * ⚠ **Refused rather than resolved either way, and that is the design.**
   * Trusting the REGISTER would let an administrative edit re-classify money
   * mid-file with no code review; trusting the CODE would keep the register
   * decorative, which is the state this refusal exists to end. So neither wins:
   * the record stops, the refusal names BOTH meanings, and a human reconciles
   * them. A disagreement about what a bill type IS cannot have a safe default.
   *
   * ⚠ **A PRS-side fault, not the sender’s** — which is why it is its own kind
   * rather than a flavour of `RECORD_MALFORMED`. The record is well-formed and
   * the indicator is one of the three; the remedy is on the Authority’s side of
   * the exchange, and telling an HSP to correct a correct record would cost it a
   * cycle to discover the advice was useless.
   *
   * ⚠ **Not an enumeration oracle.** It is decided from the record’s own
   * indicator and its service date, before any bill is looked up — the same
   * ORDER argument `RECORD_TYPE_NOT_ACCEPTED` records above, and for the same
   * reason: its answer cannot depend on whether the corpus holds anything.
   *
   * The ABSENT case is not this kind. A register silent on an indicator for the
   * service date is `NO_REFERENCE_VALUE_IN_FORCE`, which already means exactly
   * that and already carries the right remedy.
   */
  'ADJUSTMENT_INDICATOR_UNRECONCILED',
  /**
   * ★ The counterparty code declared inside the file disagrees with the channel
   * it arrived on.
   *
   * ⚠ Whole-file, always. Identity comes from the channel and never from the
   * file — this is S-019's "attribution after authentication" expressed for a
   * batch: a party that has not authenticated as one provider must not be able
   * to cause a row attributed to that provider by writing its code in a header.
   */
  'COUNTERPARTY_MISMATCH',
  /**
   * ★ The trailer's declared record count or signed amount total disagrees with
   * what was parsed.
   *
   * ⚠ Whole-file, and this direction is not negotiable: a control-total
   * mismatch means we cannot trust that we parsed ANY record correctly, so
   * accepting the ones that happened to parse is precisely the failure the
   * control exists to prevent.
   */
  'CONTROL_TOTAL_MISMATCH',
  /** The layout identifier or version in the file header is not one PRS serves. Whole-file. */
  'LAYOUT_VERSION_UNSUPPORTED',
  /**
   * ★ The exchange channel this record arrived on is not registered to accept
   * this KIND of record.
   *
   * ⚠ **Per record, never whole-file.** One HSP delivery carries new bills and
   * both directions of adjustment together (A-14, and P2C1 §12 names exactly one
   * HSP input file), and the four-axis grant model scopes an identity by BILL
   * TYPE as well as by agency (Annex A story 37). So a channel registered for new
   * bills only must refuse the adjustments inside a delivery and accept the bills
   * beside them.
   *
   * ⚠ **Added rather than folded into `RECORD_MALFORMED`, and that is the point
   * of a typed union.** The record is not malformed — it is well-formed and not
   * accepted here — and `RECORD_MALFORMED`'s remedy ("correct this record against
   * the published layout") would send the sender to check a layout that is
   * already right. A refusal whose remedy is wrong is worse than one with no
   * remedy: it costs the reader a cycle to find out it was useless.
   *
   * ⚠ **Not an enumeration oracle**, and the reason is the ORDER rather than the
   * kind: authorisation is decided from the record's own fields — its adjustment
   * indicator and the counterparty the channel resolved to — **before** anything
   * is looked up. Its answer is therefore independent of whether any bill the
   * record names exists, and a sender learns nothing about the corpus from it. An
   * implementation that authorised AFTER resolving the target would break that,
   * and would need this kind folded into `ADJUSTMENT_TARGET_UNAVAILABLE` instead.
   */
  'RECORD_TYPE_NOT_ACCEPTED',

  // ══════════════════════════════════════════════════════════════════════════
  // PAYMENT STATUS — Annex A story 7.
  // ══════════════════════════════════════════════════════════════════════════

  /**
   * ★ The bill this payment record names cannot receive a payment.
   *
   * ⚠ **ONE kind covering FIVE internal causes, and it must never be split** —
   * the same discipline, and the same rule, as
   * {@link ADJUSTMENT_TARGET_UNAVAILABLE}. The bill does not exist; it exists but
   * is outside the scope this payment file was ingested under; it was never
   * emitted to anybody; it is `REJECTED`; or the ingesting identity holds no
   * grant covering its bill type. Distinguishing them outwardly would let a
   * Payment Agency walk references and learn which ones are real.
   *
   * ⚠ **The authorisation refusal is folded in here deliberately, and that is a
   * departure from `RECORD_TYPE_NOT_ACCEPTED`'s reasoning.** On the HSP path the
   * authorisation question is answered from the record's own fields *before* any
   * lookup, so its answer cannot depend on whether a bill exists and it is safe as
   * its own kind. A payment record names a bill belonging to a HEALTHCARE SERVICE
   * PROVIDER while arriving on a PAYMENT AGENCY's channel, so the agency the
   * grant model binds on is not knowable until the bill has been resolved — and a
   * distinct kind after the lookup would be exactly the oracle assembled out of
   * two individually reasonable refusals. The audit row still reads `DENIED`;
   * D6's split between what the server decided and what the sender was told is
   * what makes that possible.
   */
  'PAYMENT_TARGET_UNAVAILABLE',
  /**
   * ★ Applying this payment would take the total paid on the bill above what the
   * bill is worth.
   *
   * ⚠ Enforced at the database and refused here, in that order of authority. A
   * Payment Agency's return file may never over-pay a bill:
   * `app.bill_payment_never_exceeds_bill` (`018_payment_status.sql`) refuses it
   * for every writer, and the grammar refuses it per record so that the refusal
   * reaches the sender as a reason it can act on rather than as a
   * `check_violation` that aborts the whole delivery.
   *
   * ⚠ Reachable only for a bill the sender can already read in full, so naming
   * the balance in the refusal discloses nothing — which is why this is its own
   * kind and not folded into {@link PAYMENT_TARGET_UNAVAILABLE}.
   *
   * ⚠ **The mirror case is NOT this kind and is not a refusal at all.** A
   * DOWNWARD ADJUSTMENT that takes what is owed below money that has already
   * moved is accepted, and the bill's settlement state becomes `OVERPAID`.
   * Refusing it would strand a legitimate correction on an already-paid bill and
   * make a real over-payment invisible; see `018_payment_status.sql`'s header for
   * the full argument.
   */
  'PAYMENT_EXCEEDS_BILL',
  /**
   * The payment status declared on the record disagrees with what its amount does
   * to the bill.
   *
   * The declared status is *the state of the bill after this instalment* — a
   * second event completing a partly-paid bill declares `PAID`, not
   * `PARTIALLY_PAID`. The layout is ours (Q-24), so that sentence is the
   * specification rather than an interpretation of one.
   *
   * ⚠ Refused rather than corrected, and refused rather than ignored. Two fields
   * are two claims about one fact and PRS is not entitled to pick between them —
   * the same argument {@link ADJUSTMENT_SERVICE_DATE_MISMATCH} already carries.
   * Deriving the state from the money alone would silently repair a Payment
   * Agency's mis-keyed status word and leave its own reconciliation disagreeing
   * with PRS's, with nothing anywhere saying so.
   */
  'PAYMENT_STATUS_INCONSISTENT',
] as const

export type RefusalKind = (typeof REFUSAL_KINDS)[number]

const KNOWN_KINDS: ReadonlySet<string> = new Set<string>(REFUSAL_KINDS)

/**
 * A refusal the caller can be told about, tagged with WHICH refusal it is.
 *
 * Extends `Error` so every existing `catch`/`instanceof Error` path — notably
 * `replay()`, which reports `error.message` per bill — keeps working unchanged,
 * and so a refusal that nobody maps still surfaces with a stack.
 *
 * ⚠ Every message here is domain text this repository wrote. **No driver
 * message, SQL fragment or connection detail ever becomes a `DomainRefusal`**,
 * because these messages are rendered to the caller verbatim. A `pg` error is
 * not a refusal; it reaches the global handler and is sanitised to a bare 500.
 */
export class DomainRefusal extends Error {
  /**
   * A structural brand, checked by {@link isDomainRefusal} alongside
   * `instanceof`.
   *
   * `instanceof` alone is not sufficient across a workspace: if two copies of
   * `@prs2/contracts` are ever resolved (a hoisting change, a stale
   * `node_modules`, a bundled copy in `apps/officer-web`), the class identity
   * differs and `instanceof` silently returns false — which would send every
   * refusal back to being a 500, i.e. re-introduce I3 with no test failing.
   * A brand plus a validated `kind` survives that.
   */
  readonly isPrs2DomainRefusal = true as const
  readonly kind: RefusalKind

  constructor(kind: RefusalKind, message: string) {
    super(message)
    this.name = 'DomainRefusal'
    this.kind = kind
  }
}

/** Is this a domain refusal — a 4xx the caller can act on, not a server fault? */
export function isDomainRefusal(error: unknown): error is DomainRefusal {
  if (!(error instanceof Error)) return false
  const candidate = error as Partial<DomainRefusal>
  return (
    candidate.isPrs2DomainRefusal === true &&
    typeof candidate.kind === 'string' &&
    KNOWN_KINDS.has(candidate.kind)
  )
}
